import { useState, useCallback, useMemo } from "react";
import type { TreeNodeData, CheckedKeys } from "../types";
import { toggleCheckCascade } from "../utils/checkCascade";
import { findNode } from "../utils/adapter";

interface UseTreeCheckOptions {
  checkable: boolean;
  checkedKeys?: string[] | CheckedKeys;
  defaultCheckedKeys?: string[];
  checkStrictly?: boolean;
  treeData: TreeNodeData[];
  onCheck?: (
    keys: string[] | CheckedKeys,
    info: { checked: boolean; node: TreeNodeData }
  ) => void;
}

const normalizeKeys = (
  keys: string[] | CheckedKeys
): { checked: string[]; halfChecked: string[] } => {
  if (Array.isArray(keys)) return { checked: keys, halfChecked: [] };

  return keys;
};

export const useTreeCheck = ({
  checkable,
  checkedKeys: controlledKeys,
  defaultCheckedKeys,
  checkStrictly = false,
  treeData,
  onCheck,
}: UseTreeCheckOptions) => {
  const isControlled = controlledKeys !== undefined;

  const [internalChecked, setInternalChecked] = useState<Set<string>>(
    () => new Set(defaultCheckedKeys ?? [])
  );
  const [internalHalf, setInternalHalf] = useState<Set<string>>(new Set());

  const { checkedSet, halfSet } = useMemo(() => {
    if (isControlled) {
      const { checked, halfChecked } = normalizeKeys(controlledKeys);

      return { checkedSet: new Set(checked), halfSet: new Set(halfChecked) };
    }

    return { checkedSet: internalChecked, halfSet: internalHalf };
  }, [isControlled, controlledKeys, internalChecked, internalHalf]);

  const handleCheckToggle = useCallback(
    (key: string) => {
      if (!checkable) return;
      const isChecking = !checkedSet.has(key);
      const node = findNode(key, treeData);

      if (checkStrictly) {
        const newChecked = new Set(checkedSet);
        if (isChecking) {
          newChecked.add(key);
        } else {
          newChecked.delete(key);
        }
        if (!isControlled) setInternalChecked(newChecked);
        onCheck?.(Array.from(newChecked), {
          checked: isChecking,
          node: node!,
        });
      } else {
        const result = toggleCheckCascade(
          key,
          isChecking,
          treeData,
          checkedSet
        );
        if (!isControlled) {
          setInternalChecked(new Set(result.checked));
          setInternalHalf(new Set(result.halfChecked));
        }
        onCheck?.(result, { checked: isChecking, node: node! });
      }
    },
    [checkable, checkedSet, checkStrictly, treeData, isControlled, onCheck]
  );

  return {
    checkedKeys: checkedSet,
    halfCheckedKeys: halfSet,
    handleCheckToggle,
  };
};
